import {FC, useEffect, useMemo, useState} from 'react';
import {Box, FormControlLabel, Grid, SelectChangeEvent, Switch, Typography} from '@mui/material';
import {Form, Formik, FormikConfig, FormikHandlers, FormikProps} from 'formik';
import {FormattedMessage, useIntl} from 'react-intl';
import {companyApi, systemApi, usersApi} from 'src/api';
import {useAppDispatch, useAppSelector} from 'src/app/hooks';
import {AMPInput} from 'src/components/common/AMPInput';
import {CancelButton} from 'src/components/common/CancelButton';
import {SubmitButton} from 'src/components/common/SubmitButton';
import {getHelperText} from 'src/utils/forms';
import {editCompanyFormSchema} from '../formData/editUser';
import {allRegionsValue} from '../../../utils/consts';
import {CompanyEditionRequest, SelectValue, UserDTO} from '../../../types';
import {EditCompanyFormTypes} from './EditCompanyForm.types';
import {actionsWrapperSx, formSx} from './EditCompanyForm.sx';
import {softSwitchSx} from 'src/components/common/switch.sx';
import {isErrorObject, processedLanguages} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {EditCompanyFormType} from '../formData/company';
import {LanguageEnum} from 'src/enums';
import {AMPFormSelect} from 'src/components/common/AMPFormSelect';
import {handleGetCountries} from 'src/hooks/useGetCountries';
import {TariffType} from '../formData/tariff';
import {AMPPhoneNumber} from 'src/components/common/AMPPhoneNumber';

type EditCompanyMutationTrigger = ReturnType<typeof companyApi.useUpdateCompanyMutation>[0];

const handleSubmit = (
    trigger: EditCompanyMutationTrigger,
    userData: UserDTO | undefined,
    handleClose: () => void,
    refetch: () => void
): FormikConfig<EditCompanyFormType>['onSubmit'] => {
    return async (value) => {
        if (!userData || !userData?.company) {
            return;
        }

        const input: CompanyEditionRequest = {
            id: userData.company.id ?? 0,
            body: {
                billingAddress: {
                    city: value.billingAddressCity,
                    country: value.billingAddressCountry,
                    houseNumber: value.billingAddressHouseNumber,
                    region: value.billingAddressRegion,
                    street: value.billingAddressStreet,
                    zipCode: value.billingAddressZipCode,
                },
                billingEmail: value.billingEmail,
                chargeConsumption: value.chargeConsumption,
                businessId: value.businessId,
                companyName: value.companyName,
                contactPerson: {
                    firstName: value.contactPersonFirstName,
                    lastName: value.contactPersonLastName,
                    phone: value.contactPersonPhone,
                },
                language: value.language,
                parkingAddress: userData.company.parkingAddress,
            },
        };

        try {
            await trigger(input).unwrap();

            refetch();
            handleClose();
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        }
    };
};

export const EditCompanyForm: FC<EditCompanyFormTypes> = ({handleClose, userData}) => {
    const {refetch} = usersApi.useGetUserByIdQuery({
        businessId: userData?.business?.id ?? 0,
        userId: userData?.id ?? 0,
    });

    const intl = useIntl();
    const dispatch = useAppDispatch();

    const {lang} = useAppSelector((state) => state.common);

    const [trigger] = companyApi.useUpdateCompanyMutation();
    const [getCountries, countries] = systemApi.useLazyGetCountriesQuery();

    const [activeCountry, setActiveCountry] = useState<string>('');
    const [activeRegion, setActiveRegion] = useState<string>('');

    const handleChangeRegion =
        (handleChange: FormikHandlers['handleChange']) => (event: SelectChangeEvent) => {
            setActiveRegion(event.target.value);
            return handleChange(event);
        };

    const handleChangeCountry =
        (
            handleChange: FormikHandlers['handleChange'],
            setFieldValue: FormikProps<TariffType>['setFieldValue']
        ) =>
            (event: SelectChangeEvent) => {
                setActiveCountry(event.target.value);
                setFieldValue('billingAddressRegion', '');
                return handleChange(event);
            };

    const handleChangePhone =
        (setFieldValue: FormikProps<TariffType>['setFieldValue'], fieldName: string) =>
            (newValue: string | number | null | undefined) => {
                if (newValue) {
                    setFieldValue(fieldName, `+${newValue}`);
                }
            };

    const processedRegions = useMemo(() => {
        const initialArray: SelectValue[] = [
            {
                id: 0,
                name: activeCountry.length ? intl.formatMessage({id: activeCountry.toLowerCase()}) : '',
                value: allRegionsValue,
            },
        ];

        if (activeCountry.length) {
            const mappedRegions: SelectValue[] | undefined = countries.data
                ?.find((country) => country.name === activeCountry)
                ?.regions?.map((region) => ({
                    id: region.id,
                    name: intl.formatMessage({id: region.name}),
                    value: region.name,
                }));

            return mappedRegions?.concat(initialArray[0]) ?? initialArray;
        }

        return initialArray;
    }, [activeCountry, lang, countries]);

    const editCompanyInitialValue = useMemo<EditCompanyFormType | undefined>(() => {
        if (!userData) {
            return;
        }

        setActiveCountry(userData?.company?.billingAddress.country ?? '');
        setActiveRegion(userData?.company?.billingAddress.region ?? '');

        return {
            businessId: userData?.business?.id ?? 0,
            companyName: userData?.company?.name ?? '',
            billingEmail: userData?.company?.billingEmail ?? '',
            billingAddressCountry: userData?.company?.billingAddress.country ?? '',
            billingAddressRegion: userData?.company?.billingAddress.region ?? '',
            billingAddressCity: userData?.company?.billingAddress.city ?? '',
            billingAddressStreet: userData?.company?.billingAddress.street ?? '',
            billingAddressHouseNumber: userData?.company?.billingAddress.houseNumber ?? '',
            billingAddressZipCode: userData?.company?.billingAddress.zipCode ?? '',
            contactPersonFirstName: userData?.company?.contactPerson.firstName ?? '',
            contactPersonLastName: userData?.company?.contactPerson.lastName ?? '',
            contactPersonPhone: userData?.company?.contactPerson.phone ?? '',
            language: userData?.language ?? LanguageEnum.ENGLISH,
            // Absent reads as billed, the way the server reads it.
            chargeConsumption: userData?.company?.chargeConsumption !== false,
        };
    }, [userData]);

    useEffect(() => {
        void handleGetCountries(getCountries, dispatch);
    }, []);

    if (!editCompanyInitialValue) {
        return null;
    }

    const processedCountries =
        countries?.data?.map((country) => ({
            id: country.id,
            name: intl.formatMessage({id: country.name.toLowerCase()}),
            value: country.name,
        })) ?? [];

    return (
        <Box>
            <Formik
                initialValues={editCompanyInitialValue}
                validationSchema={editCompanyFormSchema}
                onSubmit={handleSubmit(trigger, userData, handleClose, refetch)}
            >
                {({
                      errors,
                      handleBlur,
                      handleChange,
                      handleSubmit,
                      setFieldValue,
                      isSubmitting,
                      touched,
                      values,
                  }: FormikProps<EditCompanyFormType>) => (
                    <Form
                        onSubmit={handleSubmit}
                        noValidate
                    >
                        <Box sx={formSx}>
                            <Box>
                                <Typography
                                    variant="subtitle1"
                                    color="textPrimary"
                                >
                                    <FormattedMessage id="companyDetails"/>
                                </Typography>

                                <Grid
                                    container
                                    spacing={2}
                                >
                                    <Grid
                                        lg={6}
                                        xs={12}
                                        item
                                    >
                                        {/* Company name */}
                                        <AMPInput
                                            value={values.companyName}
                                            type={'name'}
                                            label={'companyName'}
                                            input={'companyName'}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            hasError={touched.companyName && Boolean(errors.companyName)}
                                            helperText={getHelperText('companyName', touched, errors, intl)}
                                        />
                                    </Grid>

                                    <Grid
                                        lg={6}
                                        xs={12}
                                        item
                                    >
                                        {/* Billing email */}
                                        <AMPInput
                                            value={values.billingEmail || ''}
                                            type={'emailAddress'}
                                            label={'billingEmail'}
                                            input={'billingEmail'}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            hasError={touched.billingEmail && Boolean(errors.billingEmail)}
                                            helperText={getHelperText('billingEmail', touched, errors, intl)}
                                        />
                                    </Grid>

                                    <Grid
                                        lg={12}
                                        xs={12}
                                        item
                                    >
                                        {/* Language */}
                                        <AMPFormSelect
                                            data={processedLanguages(intl)}
                                            value={values.language}
                                            label={'language'}
                                            input={'language'}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            <Box>
                                <Typography
                                    variant="subtitle1"
                                    color="textPrimary"
                                >
                                    <FormattedMessage id="billingAddress"/>
                                </Typography>

                                <Grid
                                    container
                                    spacing={2}
                                >
                                    <Grid
                                        lg={6}
                                        xs={12}
                                        item
                                    >
                                        {/* Country */}
                                        <AMPFormSelect
                                            data={processedCountries}
                                            value={values.billingAddressCountry}
                                            label={'country'}
                                            input={'billingAddressCountry'}
                                            onChange={handleChangeCountry(handleChange, setFieldValue)}
                                            onBlur={handleBlur}
                                            hasError={
                                                touched.billingAddressCountry && Boolean(errors.billingAddressCountry)
                                            }
                                            helperText={getHelperText('country', touched, errors, intl)}
                                        />
                                    </Grid>

                                    <Grid
                                        lg={6}
                                        xs={12}
                                        item
                                    >
                                        {/* Region */}
                                        <AMPFormSelect
                                            data={processedRegions}
                                            value={values.billingAddressRegion}
                                            label={'region'}
                                            input={'billingAddressRegion'}
                                            disabled={!activeCountry.length}
                                            onChange={handleChangeRegion(handleChange)}
                                            onBlur={handleBlur}
                                            hasError={
                                                touched.billingAddressRegion && Boolean(errors.billingAddressRegion)
                                            }
                                            helperText={getHelperText('region', touched, errors, intl)}
                                        />
                                    </Grid>

                                    <Grid
                                        lg={6}
                                        xs={12}
                                        item
                                    >
                                        {/* City */}
                                        <AMPInput
                                            type="text"
                                            value={values.billingAddressCity}
                                            label={'city'}
                                            input={'billingAddressCity'}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            disabled={Boolean(!activeRegion)}
                                            hasError={touched.billingAddressCity && Boolean(errors.billingAddressCity)}
                                            helperText={getHelperText('billingAddressCity', touched, errors, intl)}
                                        />
                                    </Grid>

                                    <Grid
                                        lg={6}
                                        xs={12}
                                        item
                                    >
                                        {/* Street */}
                                        <AMPInput
                                            value={values.billingAddressStreet}
                                            type={'street'}
                                            label={'street'}
                                            input={'billingAddressStreet'}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            hasError={
                                                touched.billingAddressStreet && Boolean(errors.billingAddressStreet)
                                            }
                                            helperText={getHelperText('billingAddressStreet', touched, errors, intl)}
                                        />
                                    </Grid>

                                    <Grid
                                        lg={6}
                                        xs={12}
                                        item
                                    >
                                        {/* Building № */}
                                        <AMPInput
                                            value={values.billingAddressHouseNumber}
                                            type={'text'}
                                            label={'houseNumber'}
                                            input={'billingAddressHouseNumber'}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            hasError={
                                                touched.billingAddressHouseNumber &&
                                                Boolean(errors.billingAddressHouseNumber)
                                            }
                                            helperText={getHelperText('billingAddressHouseNumber', touched, errors, intl)}
                                        />
                                    </Grid>

                                    <Grid
                                        lg={6}
                                        xs={12}
                                        item
                                    >
                                        {/* ZIP code */}
                                        <AMPInput
                                            value={values.billingAddressZipCode}
                                            type={'number'}
                                            label={'zipCode'}
                                            input={'billingAddressZipCode'}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            hasError={
                                                touched.billingAddressZipCode && Boolean(errors.billingAddressZipCode)
                                            }
                                            helperText={getHelperText('billingAddressZipCode', touched, errors, intl)}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            <Box>
                                <Typography
                                    variant="subtitle1"
                                    color="textPrimary"
                                >
                                    <FormattedMessage id="contactPerson"/>
                                </Typography>

                                <Grid
                                    container
                                    spacing={2}
                                >
                                    <Grid
                                        lg={6}
                                        xs={12}
                                        item
                                    >
                                        {/* First name */}
                                        <AMPInput
                                            value={values.contactPersonFirstName}
                                            type={'firstName'}
                                            label={'firstName'}
                                            input={'contactPersonFirstName'}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            hasError={
                                                touched.contactPersonFirstName && Boolean(errors.contactPersonFirstName)
                                            }
                                            helperText={getHelperText('contactPersonFirstName', touched, errors, intl)}
                                        />
                                    </Grid>

                                    <Grid
                                        lg={6}
                                        xs={12}
                                        item
                                    >
                                        {/* Last name */}
                                        <AMPInput
                                            value={values.contactPersonLastName}
                                            type={'lastName'}
                                            label={'contactPersonLastName'}
                                            input={'contactPersonLastName'}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            hasError={
                                                touched.contactPersonLastName && Boolean(errors.contactPersonLastName)
                                            }
                                            helperText={getHelperText('contactPersonLastName', touched, errors, intl)}
                                        />
                                    </Grid>


                                    <Grid
                                        lg={6}
                                        xs={12}
                                        item
                                    >
                                        {/* Phone number */}
                                        <AMPPhoneNumber
                                            value={values.contactPersonPhone}
                                            onChange={handleChangePhone(setFieldValue, 'contactPersonPhone')}
                                            label={'contactPersonPhone'}
                                            onBlur={handleBlur}
                                            hasError={touched.contactPersonPhone && Boolean(errors.contactPersonPhone)}
                                            helperText={getHelperText('contactPersonPhone', touched, errors, intl)}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            <Box>
                                <Typography
                                    variant="subtitle1"
                                    color="textPrimary"
                                >
                                    <FormattedMessage id="invoiceSettings"/>
                                </Typography>

                                <FormControlLabel
                                    label={intl.formatMessage({id: 'chargeConsumption'})}
                                    control={
                                        <Switch
                                            sx={softSwitchSx}
                                            name="chargeConsumption"
                                            checked={values.chargeConsumption}
                                            onChange={handleChange}
                                        />
                                    }
                                />
                            </Box>
                        </Box>

                        <Box sx={actionsWrapperSx}>
                            <CancelButton
                                text="cancel"
                                handler={handleClose}
                            />

                            <SubmitButton
                                text={'update'}
                                color="primary"
                                isSubmitting={isSubmitting}
                            />
                        </Box>
                    </Form>
                )}
            </Formik>
        </Box>
    );
};
